import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLocationById } from '../services/api';
import { 
  Container, Typography, Grid, Card, CardContent,
  Box, CircularProgress, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomButton from './CustomButton';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
  borderRadius: 16,
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
  padding: theme.spacing(3),
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: '#2c3e50',
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 700,
  fontSize: '2rem',
}));

const StatBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '12px',
  border: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
  textAlign: 'center',
  minWidth: '140px',
  flex: 1,
}));

const StatValue = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1a1a1a',
  lineHeight: 1.2,
});

const StatLabel = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: '4px',
});

const BarContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
});

const BarLabel = styled(Typography)({
  fontSize: '0.8rem',
  color: '#666',
  minWidth: '30px',
  textAlign: 'right',
});

function ReplenishLevels() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLocationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLocationDetails = async () => {
    try {
      setLoading(true);
      const locationData = await getLocationById(id);
      setLocation(locationData);
    } catch (err) {
      console.error('Error fetching location details:', err);
      setError('Failed to fetch location details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ m: 4 }}>{error}</Typography>;
  if (!location) return <Typography color="error" sx={{ m: 4 }}>Location not found</Typography>;

  const stats = location.replenishStats;
  if (!stats) return <Typography color="error" sx={{ m: 4 }}>No replenish data available</Typography>;

  const { totals, byType } = stats;
  const coveragePct = totals.totalItems > 0 
    ? Math.round((totals.withReplenish / totals.totalItems) * 100) 
    : 0;

  return (
    <StyledContainer maxWidth="lg">
      <Title variant="h4" gutterBottom>
        Replenish Data — {location.name}
      </Title>

      {/* Summary Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Overview
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <StatBox>
                  <StatValue>{totals.totalItems}</StatValue>
                  <StatLabel>Total Items</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue sx={{ color: '#2e7d32' }}>{totals.withReplenish}</StatValue>
                  <StatLabel>With Replenish</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue sx={{ color: totals.withoutReplenish > 0 ? '#c62828' : '#666' }}>
                    {totals.withoutReplenish}
                  </StatValue>
                  <StatLabel>Without Replenish</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue>{totals.totalReplenish.toLocaleString()}</StatValue>
                  <StatLabel>Total Replenish</StatLabel>
                </StatBox>
                <StatBox>
                  <StatValue>{totals.totalInventory.toLocaleString()}</StatValue>
                  <StatLabel>Total Inventory</StatLabel>
                </StatBox>
              </Box>

              {/* Coverage bar */}
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    Replenish Coverage
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    {coveragePct}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={coveragePct} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: '#ffcdd2',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#2e7d32',
                      borderRadius: 5,
                    }
                  }} 
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="textSecondary">
                    {totals.withReplenish} items have replenish levels set
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {totals.withoutReplenish} items at 0
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Breakdown by Type */}
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Breakdown by Type
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Items</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>With Replenish</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Without</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total Replenish</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total Inventory</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Coverage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byType.map((row) => {
                      const typePct = row.totalItems > 0 
                        ? Math.round((row.withReplenish / row.totalItems) * 100) 
                        : 0;
                      return (
                        <TableRow key={row.type} hover>
                          <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>{row.type}</TableCell>
                          <TableCell align="right">{row.totalItems}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                            {row.withReplenish}
                          </TableCell>
                          <TableCell align="right" sx={{ color: row.withoutReplenish > 0 ? '#c62828' : '#666', fontWeight: 500 }}>
                            {row.withoutReplenish}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {row.totalReplenish.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{row.totalInventory.toLocaleString()}</TableCell>
                          <TableCell>
                            <BarContainer>
                              <LinearProgress 
                                variant="determinate" 
                                value={typePct} 
                                sx={{ 
                                  flex: 1,
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: '#ffcdd2',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#2e7d32',
                                    borderRadius: 4,
                                  }
                                }} 
                              />
                              <BarLabel>{typePct}%</BarLabel>
                            </BarContainer>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <CustomButton onClick={() => navigate(`/locations/${id}`)}>
          Back to Location Details
        </CustomButton>
        <CustomButton onClick={() => navigate('/optimize')}>
          Edit Individual Replenish Levels
        </CustomButton>
      </Box>
    </StyledContainer>
  );
}

export default ReplenishLevels;
